import { handleSSEStream } from "./streamingUtils";

const MAX_ERROR_DETAIL_CHARS = 500;

const truncateErrorDetail = (value: string): string =>
  value.length > MAX_ERROR_DETAIL_CHARS
    ? `${value.slice(0, MAX_ERROR_DETAIL_CHARS)}…`
    : value;

const safeResponseErrorDetail = async (resp: Response): Promise<string | undefined> => {
  try {
    const contentType = resp.headers.get("content-type") ?? "";

    if (contentType.includes("application/json")) {
      const json = (await resp.json()) as Record<string, unknown>;
      const message =
        (typeof json.message === "string" && json.message) ||
        (typeof json.detail === "string" && json.detail) ||
        (typeof json.error === "string" && json.error) ||
        undefined;

      return message ? truncateErrorDetail(message) : undefined;
    }

    const text = (await resp.text()).trim();
    if (!text) return undefined;

    return truncateErrorDetail(text.replace(/\s+/g, " "));
  } catch {
    return undefined;
  }
};

export type JarvisFetch = (
  input: globalThis.RequestInfo | URL,
  init?: globalThis.RequestInit,
) => Promise<Response>;

export async function createChatSession(params: {
  fetchFn: JarvisFetch;
  personaName?: string;
}): Promise<string> {
  const { fetchFn, personaName } = params;

  const resp = await fetchFn("/jarvis/create-chat-session", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(personaName ? { persona_name: personaName } : {}),
  });

  if (!resp.ok) {
    const detail = await safeResponseErrorDetail(resp);
    throw new Error(
      detail
        ? `Failed to create chat session (HTTP ${resp.status}): ${detail}`
        : `Failed to create chat session (HTTP ${resp.status})`,
    );
  }

  const json = (await resp.json()) as { chat_session_id: string };
  return json.chat_session_id;
}

export type JarvisHistoryMessage = {
  readonly message: string;
  readonly message_id: number;
  readonly message_type: string;
  readonly parent_message: number | null;
  readonly time_sent: string;
};

export type JarvisChatHistoryMessage = {
  readonly id: string;
  readonly role: "user" | "assistant";
  readonly content: string;
  readonly messageId: number;
};

export type JarvisChatHistory = {
  readonly timeCreated: string;
  readonly messages: ReadonlyArray<JarvisChatHistoryMessage>;
};

/* The upstream vocabulary is wider than the two roles the chat UI renders; anything that is not
   explicitly authored by the user is attributed to the assistant. */
function toChatRole(messageType: string): "user" | "assistant" {
  return messageType.toLowerCase() === "user" ? "user" : "assistant";
}

export async function getChatHistory(params: {
  fetchFn: JarvisFetch;
  chatSessionId: string;
  signal?: AbortSignal;
}): Promise<JarvisChatHistory> {
  const { fetchFn, chatSessionId, signal } = params;

  const resp = await fetchFn("/jarvis/get-chat-history", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ chat_session_id: chatSessionId }),
    signal,
  });

  if (!resp.ok) {
    const detail = await safeResponseErrorDetail(resp);
    throw new Error(
      detail
        ? `Failed to get chat history (HTTP ${resp.status}): ${detail}`
        : `Failed to get chat history (HTTP ${resp.status})`,
    );
  }

  const json = (await resp.json()) as {
    time_created?: string;
    messages?: ReadonlyArray<JarvisHistoryMessage>;
  };

  const rawMessages = json.messages ?? [];

  /* `time_sent` is the per-message timestamp; `message_id` breaks ties for messages that share a
     timestamp so replay order stays stable. */
  const orderedMessages = rawMessages.slice().sort((left, right) => {
    const leftTime = String(left.time_sent ?? "");
    const rightTime = String(right.time_sent ?? "");
    if (leftTime !== rightTime) return leftTime < rightTime ? -1 : 1;
    return Number(left.message_id ?? 0) - Number(right.message_id ?? 0);
  });

  const messages = orderedMessages.map((historyMessage) => ({
    id: `history-${historyMessage.message_id}`,
    role: toChatRole(String(historyMessage.message_type ?? "")),
    content: String(historyMessage.message ?? ""),
    messageId: Number(historyMessage.message_id ?? 0),
  }));

  return {
    timeCreated: String(json.time_created ?? ""),
    messages,
  };
}

export async function* sendMessageStream(params: {
  fetchFn: JarvisFetch;
  chatSessionId: string;
  message: string;
  parentMessageId?: number | null;
  signal?: AbortSignal;
  additionalContext?: Record<string, unknown>;
}): AsyncGenerator<Record<string, unknown>, void, unknown> {
  const { fetchFn, chatSessionId, message, parentMessageId, signal, additionalContext } = params;

  const body = JSON.stringify({
    chat_session_id: chatSessionId,
    parent_message_id: parentMessageId ?? null,
    message: message,
    ...(additionalContext ? { additional_context: additionalContext } : {}),
  });

  const resp = await fetchFn("/jarvis/send-message", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
    signal,
  });

  if (!resp.ok) {
    const detail = await safeResponseErrorDetail(resp);
    throw new Error(
      detail
        ? `Failed to send message (HTTP ${resp.status}): ${detail}`
        : `Failed to send message (HTTP ${resp.status})`,
    );
  }

  yield* handleSSEStream<Record<string, unknown>>(resp);
}
